import { ArchiveDocumentField, ArchiveDocumentForm } from '@app/interfaces/archive-documents'

import type { PatchedArchiveDocumentDetailRequest } from '@app/generated/api/model'
import { buildPayload, CompatibleScalarKey, NoUncoveredWriteKey, PayloadRelation, relationRef } from './payload'

/**
 * The wire body accepted by `PATCH /api/archive-documents/{uuid}/` and, once `document_uuids` is
 * added, by `POST /api/archive-documents/update-multiple/`.
 *
 * Deliberately an alias of the GENERATED model rather than a hand-written interface: every write key
 * produced below is therefore checked against the committed OpenAPI contract, so a backend rename
 * surfaces as a tsc error here instead of a silent 400 at runtime. This is the single line to change
 * if the backend renames the request component.
 */
export type ArchiveDocumentUpdatePayload = PatchedArchiveDocumentDetailRequest

/**
 * A single-document save clears the fields of other document types; a bulk save must leave them be.
 */
export type ArchiveDocumentSaveMode = 'single' | 'bulk'

/**
 * The relations. Each `field` is the model property - which is also its `ArchiveDocumentField.name`,
 * so it is what `pick()` below is keyed by.
 *
 * Enumerated by hand because these are the only fields whose wire key differs from their model
 * property: the backend resolves each FK by a lookup value (`country_code_iso`, `<relation>_id`), so
 * there is nothing to derive the mapping from.
 */
const RELATIONS = [
  { field: 'country', key: 'country_code_iso', lookup: (document: ArchiveDocumentForm) => relationRef(document.country, (country) => country.code_iso) },
  { field: 'place', key: 'place_code', lookup: (document: ArchiveDocumentForm) => relationRef(document.place, (place) => place.code) },
  { field: 'balzac_book', key: 'balzac_book_id', lookup: (document: ArchiveDocumentForm) => relationRef(document.balzac_book, (balzacBook) => balzacBook.id) },
  { field: 'program_place', key: 'program_place_id', lookup: (document: ArchiveDocumentForm) => relationRef(document.program_place, (place) => place.id) },
  { field: 'supplier', key: 'supplier_id', lookup: (document: ArchiveDocumentForm) => relationRef(document.supplier, (supplier) => supplier.id) },
  { field: 'landlord', key: 'landlord_id', lookup: (document: ArchiveDocumentForm) => relationRef(document.landlord, (landlord) => landlord.id) },
  { field: 'partner', key: 'partner_id', lookup: (document: ArchiveDocumentForm) => relationRef(document.partner, (partner) => partner.id) },
] as const satisfies readonly PayloadRelation<ArchiveDocumentForm, ArchiveDocumentUpdatePayload>[]

type RelationWireKey = (typeof RELATIONS)[number]['key']

/**
 * Every writable key that is neither a relation lookup, nor `type_code`, nor `hints` - those three
 * are emitted outside the tables below. For all of them the model property, the wire key and the
 * `ArchiveDocumentField.name` are the same string.
 *
 * Dynamic form fields are deliberately not emitted: they are not represented by the generated
 * request contract.
 */
const WRITABLE_SCALARS = [
  'activity', 'asset_categories', 'asset_code', 'budget_lines', 'date', 'document_number', 'homere_code',
  'item_category', 'order_form_date', 'pmf_activity_number', 'project_codes', 'staff_name',
  'third_party_code', 'voucher_code', 'warehouse_code',
] as const satisfies readonly CompatibleScalarKey<ArchiveDocumentForm, ArchiveDocumentUpdatePayload>[]

// see NoUncoveredWriteKey for what this pins. `type_code` and `hints` are subtracted because they
// are emitted by hand below rather than through the two tables, not because they are exempt.
type UncoveredWriteKey = Exclude<
  keyof ArchiveDocumentUpdatePayload,
  (typeof WRITABLE_SCALARS)[number] | RelationWireKey | 'type_code' | 'hints'
>
export type EveryWriteKeyIsCovered = NoUncoveredWriteKey<UncoveredWriteKey>

/**
 * Build the flat save payload for an ArchiveDocumentForm.
 *
 * `documentFields` is the active ArchiveDocumentType's `archive_document_fields`, i.e. exactly the
 * fields the user can see in the form. Fields outside it are either explicitly nulled (single mode:
 * a document has one type, so data the user cannot see must not survive a type change - this is what
 * `MetadataComponent.clearWrongTypeFields()` used to do by mutating the model) or omitted entirely
 * (bulk mode: the selected documents were never individually inspected, so nulling those keys would
 * wipe unrelated data across all of them).
 *
 * Read-only keys (`id`, `uuid`, `filename`, `file_sha256`, `state`, `workflow`, `transitions`,
 * `is_modifiable`, `created_by`, `created_at`, `department`) and runtime-only dynamic fields are
 * never emitted: nothing here reads the model except through `RELATIONS` and `WRITABLE_SCALARS`.
 */
export function toArchiveDocumentUpdatePayload (
  document: ArchiveDocumentForm,
  documentFields: ArchiveDocumentField[],
  mode: ArchiveDocumentSaveMode,
): ArchiveDocumentUpdatePayload {
  const activeFieldNames = new Set(documentFields.map((field: ArchiveDocumentField) => field.name))

  // A value is only sent for a field belonging to the active type. Outside it, a single-document
  // save clears the field and a bulk save omits it (see the doc comment above). Scalars pass through
  // unchanged - deliberately not `?? null`: in bulk mode an untouched key is simply absent from the
  // model, and coercing it to null would wipe that field on every selected document.
  const pick = <T>(fieldName: string, value: T): T | null | undefined => {
    if (activeFieldNames.has(fieldName)) return value
    return mode === 'single' ? null : undefined
  }

  // annotated, not inferred: with no contextual return type here, buildPayload would infer Payload
  // from the RELATIONS/WRITABLE_SCALARS tables instead of from the contract
  const payload: ArchiveDocumentUpdatePayload = buildPayload(document, RELATIONS, WRITABLE_SCALARS, pick)

  // `type` is writable whatever the document type: it is not one of a type's own
  // archive_document_fields, so it never goes through pick() and stays out of RELATIONS.
  const typeCode = relationRef(document.type, (documentType) => documentType.code)
  if (typeCode !== undefined) payload.type_code = typeCode

  // the backend drops the hints once the document has been saved, so mirror that on an individual
  // save. Never in bulk: that would clear the hints of documents the user never opened.
  if (mode === 'single') payload.hints = {}

  return payload
}
